import '../../i18n/autoTranslate';
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedCarousel = ({ 
  featuredResources, 
  onResourceClick,
  className = "" 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || featuredResources?.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredResources?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, featuredResources?.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredResources?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredResources?.length) % featuredResources?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  if (!featuredResources || featuredResources?.length === 0) return null;

  return (
    <div className={`relative rounded-3xl border border-white/10 bg-white/5 overflow-hidden ${className}`}>
      {/* Carousel Content */}
      <div className="relative h-72 md:h-96">
        {featuredResources?.map((resource, index) => (
          <div
            key={resource?.id}
            className={`
              absolute inset-0 transition-all duration-500 ease-smooth
              ${index === currentSlide ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}
            `}
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <Image
                src={resource?.thumbnail}
                alt={resource?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B12]/95 via-[#0B0B12]/55 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B12]/60 via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 h-full flex items-center">
              <div className="px-6 md:px-8 max-w-2xl">
                {/* Badge */}
                <div className="flex items-center space-x-2 mb-3">
                  <span className="px-3 py-1 text-xs font-semibold bg-white/10 border border-white/10 text-white rounded-full">
                    Featured
                  </span>
                  {resource?.isNew && (
                    <span className="px-3 py-1 text-xs font-semibold bg-gradient-to-r from-[#8B7CFF] to-[#6C63FF] text-white rounded-full">
                      New
                    </span>
                  )}
                </div>

                {/* Title */}
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 line-clamp-2">
                  {resource?.title}
                </h2>

                {/* Description */}
                <p className="text-white/75 mb-5 line-clamp-2 md:line-clamp-3 leading-relaxed">
                  {resource?.description}
                </p>

                {/* Stats */}
                <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mb-6 text-sm text-white/70">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={16} />
                    <span>{resource?.duration}</span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={16} />
                    <span>{resource?.completions} completed</span>
                  </div>
                  
                  {resource?.rating && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={16} className="text-[#8B7CFF]" fill="currentColor" />
                      <span>{resource?.rating}</span>
                    </div>
                  )}
                </div>

                {/* Action Button */}
                <Button
                  variant="default"
                  onClick={() => onResourceClick && onResourceClick(resource)}
                  className="font-medium"
                  iconName="Play"
                  iconPosition="left"
                >
                  Start Learning
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Navigation Arrows */}
      {featuredResources?.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-2xl bg-black/30 backdrop-blur-sm text-white hover:bg-white hover:text-black transition-all duration-200 flex items-center justify-center border border-white/10"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-2xl bg-black/30 backdrop-blur-sm text-white hover:bg-white hover:text-black transition-all duration-200 flex items-center justify-center border border-white/10"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        </>
      )}
      {/* Dots Indicator */}
      {featuredResources?.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-2">
          {featuredResources?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`
                w-2 h-2 rounded-full transition-all duration-200
                ${index === currentSlide 
                  ? 'bg-white w-6' :'bg-white/35 hover:bg-white/60'
                }
              `}
            />
          ))}
        </div>
      )}
      {/* Auto-play Indicator */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className={`
            w-8 h-8 rounded-full backdrop-blur-sm transition-all duration-200
            ${isAutoPlaying 
              ? 'bg-accent/20 text-accent' :'bg-black/30 text-white hover:bg-black/50'
            }
            flex items-center justify-center
          `}
        >
          <Icon name={isAutoPlaying ? "Pause" : "Play"} size={14} />
        </button>
      </div>
    </div>
  );
};

export default FeaturedCarousel;
